import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { AgentThread } from "@/AgentThread/AgentThreadSidebar/AgentThread";
import { AgentThreadByIdView } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView";
import { AIAgentThreadProvider } from "@/AgentThread/AgentThreadSidebar/AIAgentThreadContext";
import { useAuth } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import { Button } from "@/shared/foundation/primitives";

import styles from "./AdminAgentThreadViewer.module.css";
import { CopyLinkButton } from "./CopyLinkButton";
import { ReadOnlyAIAgentThreadProvider } from "./ReadOnlyAIAgentThreadProvider";

interface AdminAgentThreadViewerProps {
  threadId: string;
  onBack: () => void;
  /** Sequence number of the message to scroll to once the thread renders. */
  scrollToMessageSequenceNum?: number;
  onViewFeedbackForAgentThread?: () => void;
  /** Invoked when an embedded child of the viewer switches to a different
   *  thread. Fires from the interactive branch when the admin picks a thread from
   *  the history pane, and from the read-only branch when a fork succeeds
   *  (navigating the viewer to the admin's freshly created copy). */
  onAgentThreadSelected?: (threadId: string) => void;
  /** Fully-qualified URL to copy when the admin hits the header share
   *  button. Parent decides whether this points at the thread or at a
   *  specific feedback entry so the link matches the context the admin is
   *  viewing in. */
  shareUrl: string;
}

export function AdminAgentThreadViewer({
  threadId,
  onBack,
  scrollToMessageSequenceNum,
  onViewFeedbackForAgentThread,
  onAgentThreadSelected,
  shareUrl,
}: AdminAgentThreadViewerProps) {
  const { agentThreadService } = useDomainServices();
  const { currentUser } = useAuth();
  const {
    data: agentThread,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["adminAgentThread", threadId],
    queryFn: ({ signal }) =>
      agentThreadService.getAgentThreadById(threadId, true, {
        abortSignal: signal,
      }),
    enabled: !!threadId,
  });
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    containerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [threadId]);

  // Scroll to a specific message once the thread is loaded. We query by
  // ``data-message-sequence-num`` rather than by article position because
  // tool groups, pending tool uses, and approval cards also render as
  // ``<article>`` elements — indexing by position would land on the wrong
  // element. ``requestAnimationFrame`` defers the lookup until after React
  // has committed the DOM (the previous setTimeout(100) was a magic-number
  // race against the same render cycle).
  React.useEffect(() => {
    if (!agentThread || scrollToMessageSequenceNum === undefined) {
      return;
    }
    const handle = window.requestAnimationFrame(() => {
      const target = containerRef.current?.querySelector(
        `[data-message-sequence-num="${scrollToMessageSequenceNum}"]`,
      );
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => window.cancelAnimationFrame(handle);
  }, [agentThread, scrollToMessageSequenceNum]);

  // Memoised so passing the prop doesn't recreate the function each render —
  // the AIAgentThreadProvider effect that watches ``onThreadIdChanged`` would
  // otherwise re-run every render and treat each as a fresh handler.
  const handleAgentThreadIdChangedFromInteractive = React.useCallback(
    (nextId: string | undefined) => {
      if (nextId && nextId !== agentThread?.thread_id) {
        onAgentThreadSelected?.(nextId);
      }
    },
    [agentThread?.thread_id, onAgentThreadSelected],
  );

  // ``currentUser`` is ``null`` while auth is loading (never ``undefined``).
  // Treat the branch decision as "unresolved" until both the thread and the
  // user are loaded — the previous code briefly flashed the read-only
  // viewer and banner on the admin's own thread until auth resolved.
  const ownerCheckResolved = agentThread !== undefined && currentUser !== null;
  const isOwnAgentThread =
    ownerCheckResolved && agentThread.created_by === currentUser.user_id;

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={styles.header}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back
        </Button>
        <CopyLinkButton
          url={shareUrl}
          tooltip="Copy a share link to this view"
        />
        {onViewFeedbackForAgentThread && (
          <Button
            size="small"
            variant="outlined"
            onClick={onViewFeedbackForAgentThread}
          >
            View feedback for this thread
          </Button>
        )}
        {agentThread && (
          <div className={styles.metadata}>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Org:</span>
              <span className={styles.metadataValue}>{agentThread.org_id}</span>
            </div>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>User:</span>
              <span className={styles.metadataValue}>
                {agentThread.created_by}
              </span>
            </div>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Created:</span>
              <span className={styles.metadataValue}>
                {new Date(agentThread.created).toLocaleString()}
              </span>
            </div>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Model:</span>
              <span className={styles.metadataValue}>
                {agentThread.model_profile || "default"}
              </span>
            </div>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Messages:</span>
              <span className={styles.metadataValue}>
                {agentThread.messages.length}
              </span>
            </div>
            {agentThread.forked_from_thread_id && (
              <div className={styles.metadataItem}>
                <span className={styles.metadataLabel}>Forked from:</span>
                <span className={styles.metadataValue}>
                  {agentThread.forked_from_thread_id}
                  {agentThread.forked_from_message_sequence_num !== null &&
                  agentThread.forked_from_message_sequence_num !== undefined
                    ? ` @ msg ${agentThread.forked_from_message_sequence_num}`
                    : ""}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.agentThreadContent}>
        {isLoading && (
          <div className={styles.loadingContainer}>
            <CircularProgress size="1.5rem" />
          </div>
        )}
        {error && (
          <div className={styles.errorContainer}>
            Failed to load thread: {error.message}
          </div>
        )}
        {agentThread && ownerCheckResolved && isOwnAgentThread && (
          // Admin's own thread — render the full interactive AgentThread so they can
          // continue the conversation in place. ``key`` forces a fresh provider
          // when they jump to a different own-thread so internal state resets.
          <AIAgentThreadProvider
            key={agentThread.thread_id}
            initialThreadId={agentThread.thread_id}
            onThreadIdChanged={handleAgentThreadIdChangedFromInteractive}
          >
            <AgentThread />
          </AIAgentThreadProvider>
        )}
        {agentThread && ownerCheckResolved && !isOwnAgentThread && (
          // Read-only branch: forks navigate the viewer to the admin's new
          // copy via ``onAgentThreadClicked``. Without this, ``ForkAgentThreadButton``'s
          // success path lands on a no-op and the admin sees no change.
          <ReadOnlyAIAgentThreadProvider
            onAgentThreadClicked={onAgentThreadSelected}
          >
            <AgentThreadByIdView agentThread={agentThread} />
          </ReadOnlyAIAgentThreadProvider>
        )}
      </div>

      {agentThread && ownerCheckResolved && !isOwnAgentThread && (
        <div className={styles.readOnlyBanner}>
          Read-only view. Fork from any assistant message to continue the
          conversation in your own session.
        </div>
      )}
    </div>
  );
}
